import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import {
  getDetailedDailyDowntime,
  getDetailedDailyDowntimeTier1,
  getDowntimeCategories,
  getDowntimeSubCategories,
} from '../../services/downtime/DetailedDailyServices';
import { DetailedDailyDowntimeItem } from './DetailedDaily/DetailedDailyTypes';

interface CategoryItem {
  Key: string;
  Value: string;
}
// State interface
interface DetailedDailyState {
  loading?: boolean;
  error?: string | null;
  data?: DetailedDailyDowntimeItem[];
  categories: CategoryItem[];
  subCategories: CategoryItem[];
  /** Tracks which well/asset the in-flight or last request was for; used to ignore stale responses when switching wells. */
  lastRequestedWellId?: string | number;
  lastRequestedAssetId?: string;
}

// Initial state
const initialState: DetailedDailyState = {
  loading: false,
  error: null,
  data: [],
  categories: [],
  subCategories: [],
  lastRequestedWellId: undefined,
  lastRequestedAssetId: undefined,
};

// Async thunk (wellId can be number from WellTestIdSlice or string)
export const fetchDetailedDailyDowntime = createAsyncThunk<
  DetailedDailyDowntimeItem[],
  { wellId: string | number },
  { rejectValue: string }
>('downtime/fetchDetailedDailyDowntime', async ({ wellId }, thunkAPI) => {
  try {
    const response = await getDetailedDailyDowntime(String(wellId));
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.message || 'Failed to fetch Detailed Daily Downtime');
  }
});
// Async thunk
export const fetchDetailedDailyDowntimeTier1 = createAsyncThunk<
  DetailedDailyDowntimeItem[],
  { assetId: string },
  { rejectValue: string }
>('downtime/fetchDetailedDailyDowntimeTier1', async ({ assetId }, thunkAPI) => {
  try {
    const response = await getDetailedDailyDowntimeTier1({ assetId });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.message || 'Failed to fetch Detailed Daily Downtime');
  }
});
export const fetchDetailedDailyCategories = createAsyncThunk<CategoryItem[]>(
  'downtime/fetchDetailedDailyCategories',
  async () => {
    const response = await getDowntimeCategories();
    return response;
  },
);
export const fetchDetailedDailySubCategories = createAsyncThunk<CategoryItem[], { category: string }>(
  'downtime/fetchDetailedDailySubCategories',
  async ({ category }) => {
    const response = await getDowntimeSubCategories(category);
    return response;
  },
);

const detailedDailySlice = createSlice({
  name: 'detailedDailySlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDetailedDailyDowntime.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.lastRequestedWellId = action.meta.arg.wellId;
        state.lastRequestedAssetId = undefined;
      })
      .addCase(fetchDetailedDailyDowntime.fulfilled, (state, action) => {
        // Only apply response if it matches the current request (avoids stale data when navigating Well1 -> Well2 -> Well1)
        const isMatch = String(action.meta.arg.wellId) === String(state.lastRequestedWellId);
        if (isMatch) {
          state.loading = false;
          state.data = action.payload;
        }
        // When ignoring a stale response, leave loading unchanged (another request for current well is still in flight)
      })
      .addCase(fetchDetailedDailyDowntime.rejected, (state, action) => {
        const isMatch = action.meta.arg && String(action.meta.arg.wellId) === String(state.lastRequestedWellId);
        if (isMatch) {
          state.loading = false;
          state.error = action.payload ?? 'Error fetching detailed daily downtime';
        }
      })
      .addCase(fetchDetailedDailyDowntimeTier1.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.lastRequestedAssetId = action.meta.arg.assetId;
        state.lastRequestedWellId = undefined;
      })
      .addCase(fetchDetailedDailyDowntimeTier1.fulfilled, (state, action) => {
        const isMatch = action.meta.arg.assetId === state.lastRequestedAssetId;
        if (isMatch) {
          state.loading = false;
          state.data = action.payload;
        }
      })
      .addCase(fetchDetailedDailyDowntimeTier1.rejected, (state, action) => {
        const isMatch = action.meta.arg && action.meta.arg.assetId === state.lastRequestedAssetId;
        if (isMatch) {
          state.loading = false;
          state.error = action.payload ?? 'Error fetching detailed daily downtime tier 1';
        }
      })
      .addCase(fetchDetailedDailyCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchDetailedDailySubCategories.fulfilled, (state, action) => {
        state.subCategories = action.payload;
      });
  },
});

// Selectors
export const selectDetailedDailyDowntimeLoading = (state: RootState) => state.detailedDaily.loading;
export const selectDetailedDailyDowntime = (state: RootState) => state.detailedDaily.data;
export const selectDetailedDailyCategories = (state: RootState) => state.detailedDaily.categories;
export const selectDetailedDailySubCategories = (state: RootState) => state.detailedDaily.subCategories;

// Reducer
export default detailedDailySlice.reducer;
